import { useEffect, useRef, useState } from 'react';
import { useToastMessage } from '@equip.health/ui';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { CarePlanNoteGoalResponse } from '~/lib/types/carePlan/carePlans';
import {
  useRevertGoalStatusOnNoteMutation,
  useUpdateGoalMutation,
} from '~/store/services/carePlan';
import { useClearableDebounce } from '~/lib/hooks/useClearableDebounce';

type Args = {
  goal: CarePlanNoteGoalResponse;
  noteExternalId: string;
  treatmentEpisodeId: string;
  // Fires just before handleStatusChange dispatches the update, e.g. so the
  // ED/co-occurring cards can emit their analytics event.
  onBeforeStatusChange?: () => void;
};

type UseNoteGoalDescriptionResult = {
  description: string;
  handleRevert: () => void;
  handleStatusChange: (newStatus: string) => void;
  isDescriptionChangedAndVisible: boolean;
  isReverting: boolean;
  isUpdateGoalLoading: boolean;
  isUpdateGoalSuccess: boolean;
  onGoalDescriptionBlur: () => void;
  setDescription: (next: string) => void;
  shouldShowDescriptionBox: boolean;
};

// Shared autosave + status-change + revert lifecycle for the ED and co-occurring note-drawer goal cards.
// Both cards fire the same updateGoal / revertGoalStatusOnNote mutations and use the same body shape.
export const useNoteGoalDescription = ({
  goal,
  noteExternalId,
  treatmentEpisodeId,
  onBeforeStatusChange,
}: Args): UseNoteGoalDescriptionResult => {
  const [
    updateGoal,
    { error, isLoading: isUpdateGoalLoading, isSuccess: isUpdateGoalSuccess },
  ] = useUpdateGoalMutation();
  const [revertGoalStatus, { error: revertError, isLoading: isReverting }] =
    useRevertGoalStatusOnNoteMutation();
  const { createToast } = useToastMessage();

  // Only show the panel when the LATEST history row is a real status change on this note. M4 seeds a self-transition row on create-from-note; the from!==to filter skips it.
  const shouldShowDescriptionBox =
    goal.statusHistories.length > 0 &&
    goal.statusHistories[0].noteExternalId === noteExternalId &&
    goal.statusHistories[0].statusFrom.code !==
      goal.statusHistories[0].statusTo.code;

  const [description, setDescription] = useState(() =>
    shouldShowDescriptionBox ? (goal.description ?? '') : '',
  );
  const isDescriptionChangedAndVisible =
    shouldShowDescriptionBox && description !== (goal.description ?? '');

  const { clearDebounce: clearDescriptionAutosaveDebounce, debouncedFn } =
    useClearableDebounce(5000);
  const updateDescriptionFnRef = useRef(() => {});

  // Track the most recently committed status so a pending autosave never overwrites a just-selected status with the stale goal.status.code.
  const currentStatusRef = useRef<CarePlanGoalStatus>(goal.status.code);
  useEffect(() => {
    currentStatusRef.current = goal.status.code;
  }, [goal.status.code]);

  useEffect(() => {
    if (error) {
      createToast({
        id: 'goal-card-note-update-error',
        text: 'An error occurred updating the goal. Please try again later.',
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  useEffect(() => {
    if (revertError) {
      createToast({
        id: 'goal-card-note-revert-error',
        text: "An error occurred reverting the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [revertError, createToast]);

  useEffect(() => {
    if (isDescriptionChangedAndVisible) {
      debouncedFn(() => {
        updateDescriptionFnRef.current();
      });
    } else {
      clearDescriptionAutosaveDebounce();
    }
  }, [description, isDescriptionChangedAndVisible]);

  useEffect(() => {
    updateDescriptionFnRef.current = () => {
      updateGoal({
        body: {
          description,
          noteExternalId,
          statusCode: currentStatusRef.current,
        },
        goalId: goal.id,
        treatmentEpisodeId,
      });
    };
  }, [description, goal, noteExternalId, treatmentEpisodeId, updateGoal]);

  useEffect(() => {
    return () => {
      clearDescriptionAutosaveDebounce();
    };
  }, [clearDescriptionAutosaveDebounce]);

  const onGoalDescriptionBlur = () => {
    clearDescriptionAutosaveDebounce();
    if (isDescriptionChangedAndVisible) {
      updateDescriptionFnRef.current();
    }
  };

  const handleStatusChange = (newStatus: string) => {
    onBeforeStatusChange?.();
    // Advance the current-status ref BEFORE dispatching the mutation so any description-autosave that fires between
    // now and the server refresh sends the new status, not the pre-change goal.status.code.
    currentStatusRef.current = newStatus as CarePlanGoalStatus;

    const descriptionForStatusChange = shouldShowDescriptionBox
      ? description
      : '';

    updateGoal({
      body: {
        description: descriptionForStatusChange,
        noteExternalId,
        statusCode: newStatus as CarePlanGoalStatus,
      },
      goalId: goal.id,
      treatmentEpisodeId,
    });
  };

  const handleRevert = () => {
    revertGoalStatus({ goalId: goal.id, noteExternalId });
  };

  return {
    description,
    handleRevert,
    handleStatusChange,
    isDescriptionChangedAndVisible,
    isReverting,
    isUpdateGoalLoading,
    isUpdateGoalSuccess,
    onGoalDescriptionBlur,
    setDescription,
    shouldShowDescriptionBox,
  };
};
